import { Alert, Button, Input, Modal, Space, Tag, Tooltip, message } from 'antd'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { MBA_CHAPTER_CONTRACTS } from '@/lib/thesisFramework/chapterContracts'
import { buildGateReviewSystemPrompt } from '@/lib/thesisFramework/injectContext'
import {
  parseGateReviewJson,
  type ParsedGate,
} from '@/lib/thesisFramework/parseGateReview'
import { gateStatusLabel, shortChapterTitle } from '@/lib/thesisFramework/gateLabels'
import { collectChapterDraftFromDom } from '@/lib/thesisFramework/collectChapterDraft'
import { useThesisBriefStore } from '@/stores/thesisBriefStore'
import { useThesisWorkspaceStore } from '@/stores/thesisWorkspaceStore'
import { thesisApiBase, THESIS_MODEL_NAME } from '@/lib/thesisPlatform'
import { useSubscriptionStore } from '@/stores/subscriptionStore'
import { assertCanGenerate, deductAfterGenerate, estimateTokensFromMessages } from '@/lib/thesisQuota'
import { AIServiceType, aiProviderFactory } from '@/ai'
import type { ChapterGateStatus } from '@/lib/thesisFramework/types'

function canSubmitGate(status: ChapterGateStatus | string | undefined): boolean {
  return status !== 'locked' && status !== 'passed'
}

/** 稿面顶栏：当前章 + / 提示 + 交审（自动抓正文） */
export default function ThesisCompactToolbar() {
  const { novelId } = useParams()
  const [gateOpen, setGateOpen] = useState(false)
  const [gateBusy, setGateBusy] = useState(false)
  const [gateDraft, setGateDraft] = useState('')
  const [gateResult, setGateResult] = useState<ParsedGate | null>(null)

  const activeChapterIndex = useThesisWorkspaceStore((s) => s.activeChapterIndex)
  const toggleGateRail = useThesisWorkspaceStore((s) => s.toggleGateRail)
  const gateRailOpen = useThesisWorkspaceStore((s) => s.gateRailOpen)
  const brief = useThesisBriefStore((s) =>
    novelId ? s.byNovelId[novelId] || s.getBrief(novelId) : null,
  )
  const ensureBrief = useThesisBriefStore((s) => s.ensureBrief)
  const applyGateResult = useThesisBriefStore((s) => s.applyGateResult)
  const token = useSubscriptionStore((s) => s.token)

  const ch = activeChapterIndex ?? 1
  const gateStatus = brief?.chapterGates[String(ch)]?.status
  const activeName = shortChapterTitle(
    MBA_CHAPTER_CONTRACTS.find((c) => c.chapterIndex === ch)?.title || '绪论',
  )

  useEffect(() => {
    if (novelId) ensureBrief(novelId)
  }, [novelId, ensureBrief])

  const openGate = () => {
    const auto = collectChapterDraftFromDom(ch)
    setGateDraft(auto)
    setGateResult(null)
    setGateOpen(true)
    if (!auto) {
      message.info('未自动读到正文，请从稿面复制本章内容粘贴到下方')
    }
  }

  const runGate = async () => {
    if (!novelId || !brief) return
    if (!assertCanGenerate()) return
    if (!gateDraft.trim() || gateDraft.trim().length < 80) {
      message.warning('正文太短，请先写完本章再交审（建议至少几百字）')
      return
    }
    const base = thesisApiBase()
    if (!base) {
      message.error('未配置代付 API')
      return
    }
    setGateBusy(true)
    setGateResult(null)
    try {
      const provider = aiProviderFactory.createProvider({
        aiServiceType: AIServiceType.OpenAI,
        baseUrl: `${base}/v1`,
        apiKey: token || 'thesis-platform',
      })
      const system = buildGateReviewSystemPrompt(brief, ch)
      const messages = [
        { role: 'system' as const, content: system },
        {
          role: 'user' as const,
          content: `请对以下第${ch}章草稿做门控编审：\n\n${gateDraft.trim()}`,
        },
      ]
      let out = ''
      await provider.sendStreamingRequest(
        { model: THESIS_MODEL_NAME, messages },
        (chunk) => {
          out += chunk.text
        },
      )
      deductAfterGenerate({
        promptTokens: estimateTokensFromMessages(messages),
        completionText: out,
      })
      const parsed = parseGateReviewJson(out)
      if (!parsed) {
        message.error('交审结果无法解析，请重试')
        return
      }
      applyGateResult(novelId, ch, parsed.review, parsed.summaryIfPass)
      setGateResult(parsed)
      message.success(
        parsed.review.passed
          ? `通过（${parsed.review.alignScore}），第${ch + 1}章已解锁`
          : `未通过（${parsed.review.alignScore}），请按意见改后再交`,
      )
    } catch (e) {
      message.error(e instanceof Error ? e.message : '交审失败')
    } finally {
      setGateBusy(false)
    }
  }

  const statusTag =
    gateStatus === 'passed' ? (
      <Tag color="success">已通过</Tag>
    ) : gateStatus === 'needs_revision' ? (
      <Tag color="warning">待修改后再交</Tag>
    ) : gateStatus === 'locked' ? (
      <Tag>未解锁</Tag>
    ) : (
      <Tag>{gateStatusLabel(gateStatus)}</Tag>
    )

  return (
    <>
      <div className="thesis-compact-toolbar flex items-center gap-1.5 flex-nowrap min-w-0">
        <span className="text-sm shrink-0 px-1 truncate max-w-[7.5rem] sm:max-w-[10rem]" title={activeName}>
          {ch}. {activeName}
        </span>
        {statusTag}
        <Button
          size="small"
          type={gateRailOpen ? 'default' : 'text'}
          className="shrink-0 hidden lg:inline-flex"
          onClick={toggleGateRail}
        >
          契约
        </Button>
        <Tooltip title="输入 / 唤起续写、编审等">
          <kbd className="hidden sm:inline shrink-0 px-1.5 py-0.5 rounded border text-[11px] text-secondary cursor-default select-none">
            /
          </kbd>
        </Tooltip>
        <div className="grow min-w-0" />
        {gateStatus === 'passed' ? (
          <Button size="small" type="link" className="shrink-0 px-1" onClick={openGate}>
            再次交审
          </Button>
        ) : (
          <Button
            size="small"
            type="primary"
            className="shrink-0"
            disabled={gateStatus === 'locked'}
            title={
              gateStatus === 'locked'
                ? '请先让前一章交审通过'
                : '本章写得差不多了，交审通过后才能写下一章'
            }
            onClick={openGate}
          >
            {gateStatus === 'needs_revision' ? '再交审' : '交审'}
          </Button>
        )}
      </div>

      <Modal
        title={`第${ch}章「${activeName}」交审`}
        open={gateOpen}
        onCancel={() => setGateOpen(false)}
        width={720}
        destroyOnClose
        footer={
          <Space>
            <Button onClick={() => setGateOpen(false)}>
              {gateResult?.review.passed ? '关闭，去写下一章' : '关闭'}
            </Button>
            {canSubmitGate(gateStatus) || gateStatus === 'passed' ? (
              <Button type="primary" loading={gateBusy} onClick={() => void runGate()}>
                {gateBusy ? '正在审…' : '开始交审'}
              </Button>
            ) : null}
          </Space>
        }
      >
        <ol className="text-sm thesis-muted mb-3 pl-4 list-decimal space-y-1">
          <li>本章正文写得差不多了（不必完美）</li>
          <li>点「开始交审」——对照开题卡检查有没有跑偏</li>
          <li>
            <strong className="text-inherit">通过</strong> → 自动解锁第{ch + 1}章；
            <strong className="text-inherit">未过</strong> → 按意见改，再交一次
          </li>
        </ol>

        <div className="flex items-center justify-between gap-2 mb-2">
          <span className="text-sm font-medium">本章正文</span>
          <Button
            size="small"
            type="link"
            className="px-0"
            onClick={() => {
              const auto = collectChapterDraftFromDom(ch)
              setGateDraft(auto)
              if (!auto) message.warning('稿面里还没有本章正文')
            }}
          >
            从稿面重新抓取
          </Button>
        </div>
        <Input.TextArea
          rows={10}
          value={gateDraft}
          onChange={(e) => setGateDraft(e.target.value)}
          placeholder="应自动填入本章正文；若为空请从稿面复制粘贴…"
          disabled={gateBusy}
        />

        {gateBusy ? (
          <Alert className="mt-3" type="info" showIcon message="正在对照开题卡与章契约审阅…" />
        ) : null}

        {gateResult ? (
          <div className="mt-3 space-y-2">
            <Alert
              type={gateResult.review.passed ? 'success' : 'warning'}
              showIcon
              message={
                gateResult.review.passed
                  ? `已通过（对齐 ${gateResult.review.alignScore}），第${Math.min(ch + 1, 6)}章已解锁`
                  : `未通过（对齐 ${gateResult.review.alignScore}），请按下方意见改后再交`
              }
            />
            {gateResult.review.findings.length > 0 ? (
              <div className="text-sm">
                <div className="font-medium mb-1">观察</div>
                <ul className="list-disc pl-5 space-y-0.5 text-secondary">
                  {gateResult.review.findings.map((f) => (
                    <li key={f}>{f}</li>
                  ))}
                </ul>
              </div>
            ) : null}
            {gateResult.review.revisions.length > 0 ? (
              <div className="text-sm">
                <div className="font-medium mb-1">待改</div>
                <ul className="list-decimal pl-5 space-y-0.5">
                  {gateResult.review.revisions.map((r) => (
                    <li key={r}>{r}</li>
                  ))}
                </ul>
              </div>
            ) : null}
            {gateResult.review.passed && gateResult.summaryIfPass ? (
              <div className="text-sm">
                <div className="font-medium mb-1">章摘要（供后章）</div>
                <p className="text-secondary leading-relaxed">{gateResult.summaryIfPass}</p>
              </div>
            ) : null}
          </div>
        ) : null}
      </Modal>
    </>
  )
}
